/**
 * DelayedTasksSection Component
 *
 * Card displaying delayed tasks with a 6-column table layout.
 * Shows header with alert icon and count badge, column headers,
 * task rows (max 5), and a "Show all" footer link.
 *
 * @module pages/DashboardPage/components/DelayedTasksSection
 */

import { memo } from 'react';
import { Skeleton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconAlertTriangle, IconClipboardList } from '@tabler/icons-react';

import { ICON_SIZE_SM } from '@/constants/ui';
import { EmptyState, TableRowSkeleton } from '@/components/common';

import { DelayedTaskRow } from '../DelayedTaskRow';
import { DELAYED_TASKS_LIMIT } from './DelayedTasksSection.utils';
import type { DelayedTasksSectionProps } from './DelayedTasksSection.types';
import styles from './DelayedTasksSection.module.css';

/** Column flex values matching DelayedTaskRow layout */
const SKELETON_COLUMNS = [
  { flex: 2.5 },
  { flex: 1.8 },
  { flex: 1 },
  { flex: 1.1 },
  { flex: 2 },
  { flex: 1.3 },
] as const;

export const DelayedTasksSection = memo(function DelayedTasksSection({
  tasks,
  totalCount,
  onShowAll,
  isLoading,
}: DelayedTasksSectionProps) {
  const { t } = useTranslation();

  const visibleTasks = tasks.slice(0, DELAYED_TASKS_LIMIT);
  const showFooter = totalCount > DELAYED_TASKS_LIMIT;
  const showSkeleton = isLoading && tasks.length === 0;

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div className={styles.title}>
          <IconAlertTriangle size={ICON_SIZE_SM} />
          <span>{t('dashboard.delayedTasks.title')}</span>
        </div>
        {showSkeleton ? (
          <Skeleton height={24} width={24} circle />
        ) : (
          <span className={styles.badge}>{totalCount}</span>
        )}
      </div>
      {showSkeleton ? (
        <div className={styles.content}>
          <TableRowSkeleton columns={SKELETON_COLUMNS} count={3} showDot />
        </div>
      ) : tasks.length === 0 ? (
        <EmptyState
          size="sm"
          title={t('dashboard.emptyState.delayedTasks')}
          icon={<IconClipboardList size={32} />}
        />
      ) : (
        <>
          <div className={styles.tableHeader}>
            <span className={styles.colDot} />
            <span className={styles.colTask}>
              {t('dashboard.delayedTasks.columns.task')}
            </span>
            <span className={styles.colAssignee}>
              {t('dashboard.delayedTasks.columns.assignee')}
            </span>
            <span className={styles.colPriority}>
              {t('dashboard.delayedTasks.columns.priority')}
            </span>
            <span className={styles.colDueDate}>
              {t('dashboard.delayedTasks.columns.dueDate')}
            </span>
            <span className={styles.colLocation}>
              {t('dashboard.delayedTasks.columns.location')}
            </span>
            <span className={styles.colProgress}>
              {t('dashboard.delayedTasks.columns.progress')}
            </span>
          </div>
          <div className={styles.content}>
            {visibleTasks.map((task) => (
              <DelayedTaskRow key={task.id} task={task} />
            ))}
          </div>
          {showFooter && (
            <div className={styles.footer}>
              <button
                type="button"
                className={styles.showAllLink}
                onClick={onShowAll}
              >
                {t('dashboard.delayedTasks.showAll', { count: totalCount })}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
});

DelayedTasksSection.displayName = 'DelayedTasksSection';
